import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { timelineItemVariants } from './motionVariants';
import type { PanelTone } from './tone';
import { TONE_STYLES } from './tone';

const MotionLink = motion.create(Link);

export interface TimelineRowProps {
  tone?: PanelTone;
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  href?: string;
  onClick?: () => void;
}

const rowClasses =
  'flex items-center justify-between gap-4 p-5 bg-zinc-50 border border-zinc-100 dark:bg-zinc-900/40 dark:border-white/5 rounded-2xl transition-colors group';
const interactiveClasses = 'hover:bg-zinc-100 dark:hover:bg-zinc-800/50 cursor-pointer';

/**
 * Icon-tinted list row — generalizes the repeated "icon circle + title +
 * meta row + trailing action/date" shape used across Dashboard and Client
 * Detail's activity timeline. Bakes in `timelineItemVariants` so it plugs
 * straight into a parent `staggerContainer` without callers wiring variants.
 */
export function TimelineRow({
  tone = 'zinc',
  icon,
  title,
  subtitle,
  trailing,
  href,
  onClick,
}: TimelineRowProps): JSX.Element {
  const t = TONE_STYLES[tone];
  const isInteractive = href !== undefined || onClick !== undefined;
  const className = `${rowClasses} ${isInteractive ? interactiveClasses : ''}`;

  const content = (
    <>
      <div className="flex gap-4 min-w-0">
        <div
          className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${t.iconBg}`}
        >
          <span className={t.text}>{icon}</span>
        </div>
        <div className="flex flex-col gap-0.5 justify-center min-w-0">
          <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
            {title}
          </span>
          {subtitle !== undefined && (
            <div className="flex flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {trailing !== undefined && <div className="shrink-0 flex items-center">{trailing}</div>}
    </>
  );

  if (href !== undefined) {
    return (
      <MotionLink to={href} variants={timelineItemVariants} className={className}>
        {content}
      </MotionLink>
    );
  }

  return (
    <motion.div
      variants={timelineItemVariants}
      onClick={onClick}
      className={className}
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      {content}
    </motion.div>
  );
}
